/**
 * Create or edit a custom metric, as AgencyAnalytics' wizard: start new or
 * clone another client's, then Name, Data Type (whole numbers, decimals,
 * currency, percent), Description, Formula and Comparison Value (is an
 * increase good or bad), plus who it is for: this client or all clients.
 */

import { useState, type ReactNode } from 'react';

import type { CatalogueSource } from '../reports/reports-api.ts';
import { customApi, type CustomMetric, type DataType, type FormulaToken } from './custom-api.ts';
import { FormulaEditor } from './FormulaEditor.tsx';

const DATA_TYPES: [DataType, string][] = [
  ['INTEGER', 'Integer (whole numbers)'],
  ['FLOAT', 'Float (decimals)'],
  ['CURRENCY', 'Currency'],
  ['PERCENT', 'Percent'],
];

export function CustomMetricDialog(props: {
  organisationId: string;
  clientId: string;
  clientName: string;
  sources: CatalogueSource[];
  metric: CustomMetric | null;
  /** The agency's other metrics, to clone from. */
  others: CustomMetric[];
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const initial = props.metric;
  const [name, setName] = useState(initial?.name ?? '');
  const [dataType, setDataType] = useState<DataType>(initial?.data_type ?? 'FLOAT');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [formula, setFormula] = useState<FormulaToken[]>(initial?.formula ?? []);
  const [higherIsBetter, setHigherIsBetter] = useState(initial?.higher_is_better ?? true);
  const [forAll, setForAll] = useState(initial ? initial.business_unit_id === null : false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const clone = (id: string) => {
    const from = props.others.find((item) => item.id === id);
    if (!from) return;
    setName(`${from.name} (copy)`);
    setDataType(from.data_type);
    setDescription(from.description ?? '');
    setFormula(from.formula);
    setHigherIsBetter(from.higher_is_better);
  };
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await customApi.saveMetric(
        props.organisationId,
        {
          business_unit_id: forAll ? null : props.clientId,
          name,
          description: description || null,
          data_type: dataType,
          higher_is_better: higherIsBetter,
          formula,
        },
        initial?.id,
      );
      props.onSaved();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The metric could not be saved.');
      setBusy(false);
    }
  };

  return (
    <div className="rc-scrim">
      <div className="rc cx-dialog" role="dialog" aria-modal="true" aria-labelledby="cm-title">
        <h2 id="cm-title">{initial ? `Edit ${initial.name}` : 'New custom metric'}</h2>
        {!initial && props.others.length > 0 && (
          <label className="rs-field">
            Start from
            <select defaultValue="" onChange={(event) => clone(event.target.value)}>
              <option value="">A new custom metric</option>
              {props.others.map((item) => (
                <option key={item.id} value={item.id}>
                  Clone: {item.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="kp-two">
          <label className="rs-field">
            Name
            <input
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </label>
          <label className="rs-field">
            Data Type
            <select
              value={dataType}
              onChange={(event) => setDataType(event.target.value as DataType)}
            >
              {DATA_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="rs-field">
          Description <small>(optional)</small>
          <input
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <div className="rs-field">
          Formula
          <FormulaEditor
            tokens={formula}
            choices={{ kind: 'metrics', sources: props.sources }}
            onChange={setFormula}
          />
          <small className="kp-faint">
            Totals are worked out from the totals (sum A ÷ sum B), as in AgencyAnalytics. Choose
            Currency for money results and Percent for rates.
          </small>
        </div>
        <div className="kp-two">
          <label className="rs-field">
            Comparison Value
            <select
              value={higherIsBetter ? 'up' : 'down'}
              onChange={(event) => setHigherIsBetter(event.target.value === 'up')}
            >
              <option value="up">Increase is positive</option>
              <option value="down">Decrease is positive</option>
            </select>
          </label>
          <label className="rs-field">
            Available to
            <select
              value={forAll ? 'all' : 'one'}
              onChange={(event) => setForAll(event.target.value === 'all')}
            >
              <option value="one">{props.clientName} only</option>
              <option value="all">All clients</option>
            </select>
          </label>
        </div>
        {problem && (
          <p className="kp-problem" role="alert">
            {problem}
          </p>
        )}
        <div className="kp-dialog-actions">
          <button type="button" className="btn secondary" onClick={props.onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !name.trim() || formula.length === 0}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : initial ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
